import React from 'react';

import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { HamburgerButton } from 'components/app-header/mobile-header/hamburger-button';
import { Header, LinkNoDecoration, MenuButtonContainer } from 'components/app-header/styled';
import {
  NO_NETWORK_BACKGROUND_COLOR,
  NoNetworkHeader,
} from 'components/error-network/no-network-header';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { Logo } from 'components/logo';
import { NotificationsButton } from 'components/notifications-button';
import {
  useContentCardsNotificationsExperiment,
  useExposeContentCardsNotificationsExperiment,
} from 'experiments/use-content-cards-notifications';
import { useIsDesktopBreakpoint, useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import useAuthFlow from 'state/auth/hooks/use-auth-flow';
import { useNotificationCenterReadStatus } from 'state/braze/hooks/use-notification-center-read-status';
import { useSyncLocalReadContentCardsOnLogin } from 'state/braze/hooks/use-sync-local-read-content-cards-on-login';
import { t } from 'state/intl/translations';
import { useNetworkContext } from 'state/network';
import { primitive } from 'styles/constants';
import { routes } from 'utils/routing';

import SignupMobileHeader from '../signup-mobile-header';
import { StyledCenterSpacer, StyledLogo, StyledRightSpacer } from '../styled';

import { CartButton } from './cart-button';
import { HeaderLoyaltyPointsButton } from './mobile-header-cta';
import { ServiceModeBar } from './service-mode-bar';

export const StandardMobileHeader = React.memo(({ hideTsm = false }: { hideTsm?: boolean }) => {
  const { isNewsFeedEnabled: enableContentCards } = useContentCardsNotificationsExperiment();
  useExposeContentCardsNotificationsExperiment();
  useNotificationCenterReadStatus({ enabled: enableContentCards });
  useSyncLocalReadContentCardsOnLogin({ enabled: enableContentCards });
  const isDesktop = useIsDesktopBreakpoint();
  const { isNetworkConnected, isInternetReachable } = useNetworkContext();

  const isMobile = useIsMobileBreakpoint();

  const { isInAuthFlow } = useAuthFlow();
  const { modalAuthIsOpen } = useAuthContext();

  const insets = useSafeAreaInsets();

  if (isInAuthFlow && !modalAuthIsOpen) {
    return <SignupMobileHeader />;
  }

  const showNoNetworkHeader = !isNetworkConnected || !isInternetReachable;

  return (
    <View
      collapsable={false}
      accessibilityLabel={t('primaryNavLinks')}
      testID="mobile-header"
      style={{
        paddingTop: insets.top,
        backgroundColor:
          showNoNetworkHeader && isMobile ? NO_NETWORK_BACKGROUND_COLOR : primitive.$mayoEggWhite,
      }}
    >
      {showNoNetworkHeader && <NoNetworkHeader />}
      <CenteredRowContainer standardPadding>
        <Header>
          <MenuButtonContainer>
            <HamburgerButton />
          </MenuButtonContainer>
          <StyledCenterSpacer>
            <LinkNoDecoration
              to={routes.base}
              alignSelf={'flex-start'}
              marginLeft={isDesktop ? 'auto' : '$8'}
            >
              <StyledLogo testID="mobile-header-logo">
                <Logo />
              </StyledLogo>
            </LinkNoDecoration>
          </StyledCenterSpacer>
          <StyledRightSpacer>
            <HeaderLoyaltyPointsButton />
            {enableContentCards && <NotificationsButton />}
            <CartButton />
          </StyledRightSpacer>
        </Header>
      </CenteredRowContainer>
      <ServiceModeBar hideTsm={hideTsm} />
    </View>
  );
});
